import { FC, useMemo } from 'react';
import { IOrderBasketItem, IOrderBasketItemContent } from '../../../../components-library/basket/order-basket-item';
import { useTranslation } from 'react-i18next';
import FeedbackLabel from '../../../../components-library/labels/FeedbackLabel';
import useStore from '../../../../hooks/store/useStore';
import { REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import useBasket from '../../../../hooks/basket/useBasket';
import useAppMethods from '../../../../hooks/utility/useAppMethods';
import useTheme from '../../../../hooks/ui/useTheme';
import { OpeningHoursInterface } from '../../../../services/exports/Interfaces';
import WorkScheduleHelpers from '../../../../services/helpers/WorkScheduleHelpers';
import i18n from 'i18next';
import { Button } from '@mui/material';
import CounterButton from '../../../../components-library/buttons/CounterButton';
import PriceComparison from '../../../../components-library/menu/PriceComparison';
import OfferLineBreakdown from '../../../../features/offers/components/OfferLineBreakdown';

const OrderBasketItem: FC<IOrderBasketItem & IOrderBasketItemContent> = (props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Basket:OrderBasketItem' });

  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { data, index, hideDiscount, editable, onUpdateClick, renderDescription, getSoldOutItems, withImage } = props;

  const theme = useTheme();
  const { formatScheduleUnit } = useAppMethods();
  const { incrementItem, decrementItem } = useBasket();
  const hasImage = withImage && !!data.entry?.thumbnail_url;
  const renderWarnings = useMemo(() => {
    if (!editable) {
      return null;
    }

    if (data.is_sold_out) {
      const soldOutItems = getSoldOutItems();

      return (
        <FeedbackLabel
          message={t('feedback.item_sold_out', {
            count: soldOutItems.count(),
            items: soldOutItems.implode(', '),
          })}
          type="Notice"
          textClassname="text-xs"
        />
      );
    }

    return (
      !data.is_available && (
        <FeedbackLabel
          message={t('feedback.item_unavailable', {
            schedule: WorkScheduleHelpers.formatSchedule(
              data?.category?.schedule ?? data?.menu?.schedule ?? company?.work_schedule,
              (item: OpeningHoursInterface) => `${formatScheduleUnit(item.open)} - ${formatScheduleUnit(item.close)}`,
              (item: OpeningHoursInterface) =>
                `${formatScheduleUnit(item.open, { day: true, time: true })} - ${formatScheduleUnit(item.close, { day: true, time: true })}`,
            ),
          })}
          type="Notice"
          textClassname="text-xs"
        />
      )
    );
  }, [editable, data, company?.work_schedule, i18n.language]);

  return (
    <>
      <div className="w-full flex space-x-md rounded-lg bg-brand-card bordered border-xs p-sm border-brand-border">
        {hasImage && (
          <div className="rounded-[4px] overflow-hidden border-xs borrde-mobile-secondary h-fit">
            <img src={data.entry?.thumbnail_url} className="size-14" loading="lazy" alt="Product img" />
          </div>
        )}
        <div className="space-y-1 w-full">
          <div className="flex items-center justify-between">
            <h6
              className="text-sm break-words line-clamp-2 font-normal"
              style={{
                wordBreak: 'break-word',
              }}
            >
              {data.entry?.name}
            </h6>
            <div>
              <PriceComparison
                className="!ml-auto self-start text-muted-foreground"
                originalPrice={data.original_price}
                actualPrice={data.actual_price}
                hideDiscount={hideDiscount}
                originalPriceStyle={{
                  fontSize: theme.font.size.sm,
                }}
                actualPriceStyle={{
                  fontSize: theme.font.size.sm,
                }}
              />
            </div>
          </div>
          <OfferLineBreakdown item={data} className="text-end" />
          {data.note && <p className="text-xs text-muted-foreground">{data.note}</p>}
          {renderDescription()}
          {editable && (
            <Button
              onClick={onUpdateClick}
              disabled={!editable || !onUpdateClick}
              className="!min-w-fit !ps-0 !pe-0 !mb-1 h-[10px] !capitalize !font-normal !text-xs !text-brand-primary"
            >
              Update
            </Button>
          )}
          {editable && (
            <CounterButton
              count={data.quantity}
              increase={() => incrementItem(index)}
              decrease={() => decrementItem(index)}
              allowZero
              iconColor={theme.color.gray['300']}
              classes={{
                container:
                  'h-8 min-w-[124px] !mt-sm !w-fit flex justify-between items-center border-xs px-sm py-xs shadow-none',
                quantity: 'text-md font-semibold mx-lg',
                buttons: 'w-4',
              }}
            />
          )}
          {!editable && <h6 className="text-brand-primary font-semibold text-end">{data.quantity}x</h6>}
          {!!data.badges?.length && (
            <div className="pt-1">
              {data.badges.map((badge) => (
                <p
                  key={badge}
                  className="w-fit bg-brand-primary/30 text-xs p-1 rounded flex items-center justify-center text-brand-primary font-medium"
                >
                  {badge}
                </p>
              ))}
            </div>
          )}
          {renderWarnings}
        </div>
      </div>
    </>
  );
};

export default OrderBasketItem;
